import {
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StoreContext } from 'stores/index';
import { LogonResult } from 'stores/models/LogonResult';
import { validateEmail } from 'utilities/validateEmail';

export interface LogonProps {}

const useStyles = makeStyles((theme: Theme) => ({
  bottomButtons: {
    marginTop: theme.spacing(1),
  },
}));

const Logon: React.FunctionComponent<LogonProps> = observer(() => {
  const { userStore } = React.useContext(StoreContext);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const styles = useStyles();

  if (userStore.isAuthenticated) {
    navigate('/');
    return <React.Fragment />;
  }

  const handleLogon = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (submitting) {
      // Avoid double submits (e.g. impatient double-clicking while the
      // backend is slow / cold-starting) which can trip Cognito throttling
      // and surface as a confusing "wrong password" error.
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await userStore.login(username, password, navigate);

      switch (result.status) {
        case LogonResult.success:
          navigate('/');
          break;
        case LogonResult.failure:
          setError(result.message);
          break;
        case LogonResult.newPassword:
          // Do NOT pass result.user through navigate's state. CognitoUser
          // instances contain non-cloneable properties (e.g. its internal
          // `storage`, which is a function), and react-router's history
          // library calls the browser's native history.pushState() under
          // the hood. That call performs a structured-clone of the state
          // object and throws when it hits a function/non-cloneable value;
          // history then silently falls back to a full window.location
          // reload, wiping all in-memory JS state (including the very
          // user object we were trying to pass along). This was the root
          // cause of the "result.user is deallocated upon page switch"
          // bug. userStore.login() already stashes the CognitoUser on
          // userStore.pendingNewPasswordUser, so we don't need to (and must
          // not) pass it here.
          navigate('/logon/new_password');
          break;
        case LogonResult.userDoesNotExist:
          setError(result.message);
          break;
        case LogonResult.wrongPassword:
          setError('Du har indtastet forkert bruger eller kodeord');
          break;
        case LogonResult.passwordResetRequired:
          navigate('/logon/forgot_password', { state: { username } });
          break;
        case LogonResult.requiresConfirmation:
          navigate('/create_user/confirm_code', {
            state: { email: username },
          });
          break;
        case LogonResult.acceptTerms:
          break;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: '1em' }}>
      <Container maxWidth="xs">
        <Typography variant="h5">Log på</Typography>
        <form noValidate onSubmit={handleLogon}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('logon.email_address')}
            name="email"
            autoComplete="email"
            onChange={(e: any) => {
              setUsername(e.currentTarget.value);
            }}
            autoFocus
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('logon.password')}
            type="password"
            id="password"
            onChange={(e: any) => {
              setPassword(e.currentTarget.value);
            }}
            autoComplete="current-password"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={validateEmail(username) === false || submitting}
          >
            {submitting ? t('generic.loading') : t('generic.logon')}
          </Button>
          <Grid container className={styles.bottomButtons}>
            <Grid item xs>
              <Link
                style={{ cursor: 'pointer' }}
                variant="body2"
                onClick={() =>
                  navigate('/logon/forgot_password', { state: { username } })
                }
              >
                {t('logon.forgotten_password')}
              </Link>
            </Grid>
            <Grid item>
              <Link
                style={{ cursor: 'pointer' }}
                variant="body2"
                onClick={() => navigate('/logon/create_user')}
              >
                {t('logon.create_account')}
              </Link>
            </Grid>
          </Grid>
          {(error || userStore.sessionRestoreError) && (
            <Typography variant="body2" color="error">
              {error || userStore.sessionRestoreError}
            </Typography>
          )}
        </form>
      </Container>
    </div>
  );
});

export default Logon;
